import React, { useEffect, useState } from "react";
import { api, formatApiError } from "../lib/api";
import { useAuth } from "../contexts/AuthContext";
import { Wrench, BookmarkPlus, Loader2 } from "lucide-react";
import { toast } from "sonner";

/**
 * Compact, queue-card-friendly availability strip that pings
 * /api/equipment/availability for each comma/newline-separated token in `text`
 * and renders a small pill per token showing live tool availability.
 *
 * Optional `shipmentId` enables the "Reserve for me" button — one tap
 * auto-checks out the best-available linked tool to the current user with
 * Expected_Return_Date = the shipment's ETA.
 *
 * Used inside Equipment Needed banners on PreparationQueue cards.
 */
export default function ToolAvailabilityRow({ text, shipmentId, onReserved }) {
  const { isCrewLeader, isRunnerLevel } = useAuth();
  const [avail, setAvail] = useState({});
  const [loading, setLoading] = useState(false);
  const [reserving, setReserving] = useState(false);
  const [reloadTick, setReloadTick] = useState(0);

  const tokens = (text || "")
    .split(/[,\n;]/)
    .map((t) => t.trim())
    .filter((t) => t.length >= 2);

  useEffect(() => {
    if (!text || !text.trim()) {
      setAvail({});
      return;
    }
    if (tokens.length === 0) return;
    let cancelled = false;
    setLoading(true);
    const handle = setTimeout(async () => {
      try {
        const { data } = await api.get("/equipment/availability", {
          params: { names: tokens.join(",") },
        });
        if (!cancelled) setAvail(data || {});
      } catch {
        /* ignore */
      } finally {
        if (!cancelled) setLoading(false);
      }
    }, 250);
    return () => {
      cancelled = true;
      clearTimeout(handle);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [text, reloadTick]);

  const tracked = tokens
    .map((tok) => ({ tok, info: avail[tok] }))
    .filter((row) => row.info && row.info.total > 0);

  // "Reserve for me" is only useful when the shipment context is present, the
  // user can be assigned a tool, and at least one token has at least one
  // available tool to grab.
  const canReserve =
    !!shipmentId && (isCrewLeader || isRunnerLevel) && tracked.some((r) => r.info.available > 0);

  const onReserve = async (e) => {
    e?.preventDefault?.();
    e?.stopPropagation?.();
    if (reserving || !canReserve) return;
    setReserving(true);
    try {
      const { data } = await api.post(`/shipments/${shipmentId}/reserve-equipment`, {});
      const reserved = data.reserved || [];
      const skipped = data.skipped || [];
      if (reserved.length > 0) {
        const summary = reserved.map((r) => r.Tool_Name).join(", ");
        toast.success(`Reserved ${reserved.length} tool${reserved.length === 1 ? "" : "s"}: ${summary}`);
      }
      if (skipped.length > 0) {
        const labels = skipped
          .map((s) =>
            s.reason === "no_tracked_tool"
              ? `${s.token} (no tracked tool)`
              : `${s.token} (none available)`
          )
          .join(", ");
        if (reserved.length === 0) {
          toast.error(`Could not reserve: ${labels}`);
        } else {
          toast.warning(`Skipped: ${labels}`);
        }
      }
      // Force a refresh so the pills reflect the new availability counts
      setReloadTick((t) => t + 1);
      onReserved?.(data);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Reservation failed");
    } finally {
      setReserving(false);
    }
  };

  if (tracked.length === 0 && !canReserve) return null;

  return (
    <div className="mt-1 flex flex-wrap gap-1.5 items-center" data-testid="tool-avail-row">
      <Wrench className="h-3 w-3 text-amber-700" />
      {tracked.map(({ tok, info }) => {
        const ok = info.available > 0;
        return (
          <span
            key={tok}
            data-testid={`tool-avail-pill-${tok}`}
            title={`${info.available} available · ${info.checked_out} checked out · ${info.total} total`}
            className={`text-[10px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded-sm border ${
              ok
                ? "bg-emerald-50 border-emerald-300 text-emerald-800"
                : "bg-rose-50 border-rose-300 text-rose-800"
            }`}
          >
            {tok}: {info.available}/{info.total}
          </span>
        );
      })}
      {canReserve && (
        <button
          type="button"
          onClick={onReserve}
          disabled={reserving || loading}
          data-testid={`reserve-for-me-btn-${shipmentId}`}
          className="rg-tap text-[10px] font-bold uppercase tracking-wider px-2 py-1 rounded-sm border-2 bg-amber-600 hover:bg-amber-700 active:bg-amber-800 text-white border-amber-800 inline-flex items-center gap-1 disabled:opacity-60 disabled:cursor-not-allowed"
          title="One-tap check-out the best available tool(s) to me"
        >
          {reserving ? (
            <Loader2 className="h-3 w-3 animate-spin" />
          ) : (
            <BookmarkPlus strokeWidth={3} className="h-3 w-3" />
          )}
          {reserving ? "Reserving…" : "Reserve for me"}
        </button>
      )}
    </div>
  );
}
